<template>
  <div
    class="DatePickerExceptional"
    :class="{
      'DatePickerExceptional--edit': isNewEvent,
      'DatePickerExceptional--cancels': value.toCancelAppointments.length > 0
    }"
  >
    <v-row align="center" class="px-6 py-1">
      <v-col class="DatePickerExceptional-dateField">
        <v-menu
          v-model="menu"
          :close-on-content-click="false"
          max-width="290px"
          min-width="290px"
          offset-y
          transition="scale-transition"
          :disabled="!isNewEvent"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              :value="dateFormatted"
              v-on="on"
              class="mt-0 pt-0"
              hide-details
              :placeholder="$t('datePickerExceptional.datePickerLabel')"
              prepend-icon="mdi-calendar"
              readonly
            />
          </template>
          <v-date-picker v-model="date" :range="value.type === 'range'" :selected-items-text="rangeText" :color="isTransitions ? 'transitionsPrimary' : 'primaryAccent'">
            <v-btn block text color="primary" @click="menu = false">{{
              $t('datePickerExceptional.validButton')
            }}</v-btn>
          </v-date-picker>
        </v-menu>
      </v-col>
      <v-col class="text-center red--text">
        <v-btn
          v-if="value.toCancelAppointments.length > 0"
          @click="$emit('clickCanceled')"
          class="subtitle-2 text-none red--text"
          text
        >
          <v-icon color="red" left size="25px">mdi-alert-circle</v-icon>
          <span v-if="$vuetify.breakpoint.sm">{{ value.toCancelAppointments.length }}</span>
          <span v-else>
            {{
              $tc('datePickerExceptional.appointmentAffected', value.toCancelAppointments.length, {
                value: value.toCancelAppointments.length
              })
            }}
          </span>
        </v-btn>
      </v-col>
      <v-col class="flex-grow-0 d-flex justify-space-between align-center font-weight-bold">
        {{ $t(`datePickerExceptional.eventType.${value.eventType}`) }}
        <v-btn @click="$emit('remove')" class="ml-3 ml-md-10" icon>
          <v-icon color="secondary">mdi-delete-outline</v-icon>
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import fnsFormat from 'date-fns/format'
import differenceInDays from 'date-fns/differenceInDays'
import subDays from 'date-fns/subDays'

export default {
  name: 'DatePickerExceptional',

  props: {
    value: {
      required: true,
      type: Object,
      validator: v => Object.keys(v).every(key => ['type', 'eventType', 'toCancelAppointments'].includes(key))
    }
  },

  data() {
    return {
      date: this.value.type === 'date' ? '' : [],
      dateFormatted: this.initDate(),
      menu: false
    }
  },

  computed: {
    isNewEvent() {
      return !this.value.exceptionalEventUuid
    },
    rangeText() {
      if (this.value.type !== 'range' || this.date.length !== 2) return ''

      const firstValue = new Date(this.date[0])
      const secondValue = new Date(this.date[1])
      return firstValue < secondValue
        ? `${this.date[0].slice(-5)} - ${this.date[1].slice(-5)}`
        : `${this.date[1].slice(-5)} - ${this.date[0].slice(-5)}`
    }
  },

  watch: {
    date() {
      if (!this.date || this.date.length === 0) return

      this.dateFormatted = this.formatDate(this.date, this.value.type)

      if (this.value.type === 'date') {
        this.$emit('input', {
          ...this.value,
          beginDateTime: new Date(this.date),
          endDateTime: new Date(this.date)
        })
      } else if (this.value.type === 'range' && this.date.length === 2) {
        const firstValue = new Date(this.date[0])
        const secondValue = new Date(this.date[1])
        const isInOrder = firstValue < secondValue
        this.$emit('input', {
          ...this.value,
          beginDateTime: isInOrder ? firstValue : secondValue,
          endDateTime: isInOrder ? secondValue : firstValue
        })
      }

      this.$emit('change')
    }
  },

  methods: {
    formatDate(date, type) {
      if (!date) return null

      if (type === 'date') {
        return fnsFormat(new Date(date), 'dd/MM/yyyy')
      } else if (type === 'range') {
        if (date.length === 1) {
          return fnsFormat(new Date(date), 'dd/MM/yyyy')
        }

        return [...date]
          .sort()
          .map(d => fnsFormat(new Date(d), 'dd/MM/yyyy'))
          .join(' -  ')
      } else {
        throw new Error('this case is not supported')
      }
    },
    initDate() {
      if (!this.value.exceptionalEventUuid) return ''

      const { beginDateTime, endDateTime } = this.value
      const isRange = differenceInDays(endDateTime, beginDateTime) > 1

      return isRange
        ? `${fnsFormat(beginDateTime, 'dd/MM/yyyy')} - ${fnsFormat(subDays(endDateTime, 1), 'dd/MM/yyyy')}`
        : `${fnsFormat(beginDateTime, 'dd/MM/yyyy')}`
    }
  }
}
</script>

<style scoped lang="scss">
.DatePickerExceptional {
  display: flex;
  background-color: var(--v-secondary-lighten2);
  &--edit {
    background-color: var(--v-secondary-lighten1);
  }
  &--cancels {
    border: 1px solid red;
  }
}

.DatePickerExceptional-dateField {
  max-width: 250px;
}
</style>
